import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@journ/ui/card'
import { RadioGroup, RadioGroupItem } from '@journ/ui/radio-group'
import { ScrollArea } from '@journ/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@journ/ui/select'
import { SubmitButton } from '@journ/ui/submit-button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@journ/ui/tabs'
import { useMemo, useState } from 'react'
import { useSelector } from '@/libs/state'
import { useIntlayer } from 'react-intlayer'
import { session$ } from '@/features/auth/store'
import { usePermissionConfig } from '../hooks/use-permission-config'
import { usePermissionConfigActions } from '../hooks/use-permission-config-actions'
import { usePermissionUsers } from '../hooks/use-permission-users'
import type {
	PermissionKey,
	PermissionUser,
	RoleId,
	RolePermissionConfig,
	UserPermissionOverride,
} from '../domain/permission'
import { draftRoleConfigs$, draftUserOverrides$ } from '../store'

type PermissionResourceKey =
	| 'hrMembers'
	| 'hrOwnProfile'
	| 'timeOffOwn'
	| 'timeOffTeam'
	| 'timeOffDepartment'
	| 'timeOffAll'
	| 'workflows'
	| 'settings'

type PermissionOptionKey = 'none' | 'read' | 'write' | 'approve' | 'manage'

type PermissionOption = {
	key: PermissionOptionKey
	grants: PermissionKey[]
}

type PermissionResource = {
	key: PermissionResourceKey
	permissions: PermissionKey[]
	options: PermissionOption[]
}

type UserOverrideOptionKey = 'inherit' | 'revoke' | PermissionOptionKey

const ROLES: RoleId[] = ['employee', 'manager', 'department_leader', 'team_leader']
const NO_USER = '__none__'

const PERMISSION_RESOURCES: PermissionResource[] = [
	{
		key: 'hrMembers',
		permissions: ['hr.members.view', 'hr.members.manage'],
		options: [
			{ key: 'none', grants: [] },
			{ key: 'read', grants: ['hr.members.view'] },
			{ key: 'write', grants: ['hr.members.view', 'hr.members.manage'] },
		],
	},
	{
		key: 'hrOwnProfile',
		permissions: ['hr.members.edit-own'],
		options: [
			{ key: 'none', grants: [] },
			{ key: 'write', grants: ['hr.members.edit-own'] },
		],
	},
	{
		key: 'timeOffOwn',
		permissions: ['hr.time-off.view-own', 'hr.time-off.request'],
		options: [
			{ key: 'none', grants: [] },
			{ key: 'read', grants: ['hr.time-off.view-own'] },
			{ key: 'write', grants: ['hr.time-off.view-own', 'hr.time-off.request'] },
		],
	},
	{
		key: 'timeOffTeam',
		permissions: ['hr.time-off.view-team', 'hr.time-off.notes'],
		options: [
			{ key: 'none', grants: [] },
			{ key: 'read', grants: ['hr.time-off.view-team'] },
			{ key: 'write', grants: ['hr.time-off.view-team', 'hr.time-off.notes'] },
		],
	},
	{
		key: 'timeOffDepartment',
		permissions: ['hr.time-off.view-dept', 'hr.time-off.approve-dept', 'hr.time-off.notes'],
		options: [
			{ key: 'none', grants: [] },
			{ key: 'read', grants: ['hr.time-off.view-dept'] },
			{ key: 'approve', grants: ['hr.time-off.view-dept', 'hr.time-off.approve-dept', 'hr.time-off.notes'] },
		],
	},
	{
		key: 'timeOffAll',
		permissions: ['hr.time-off.view-all', 'hr.time-off.approve-hr'],
		options: [
			{ key: 'none', grants: [] },
			{ key: 'read', grants: ['hr.time-off.view-all'] },
			{ key: 'approve', grants: ['hr.time-off.view-all', 'hr.time-off.approve-hr'] },
		],
	},
	{
		key: 'workflows',
		permissions: ['workflows.view', 'workflows.manage'],
		options: [
			{ key: 'none', grants: [] },
			{ key: 'read', grants: ['workflows.view'] },
			{ key: 'write', grants: ['workflows.view', 'workflows.manage'] },
		],
	},
	{
		key: 'settings',
		permissions: ['settings.permissions.manage'],
		options: [
			{ key: 'none', grants: [] },
			{ key: 'manage', grants: ['settings.permissions.manage'] },
		],
	},
]

const getRoleGrants = (roleConfigs: RolePermissionConfig[], roleId: RoleId): PermissionKey[] =>
	roleConfigs.find(r => r.roleId === roleId)?.grants ?? []

const optionMatches = (grants: Set<PermissionKey>, option: PermissionOption): boolean =>
	option.grants.every(permission => grants.has(permission))

const getRoleResourceValue = (
	roleConfigs: RolePermissionConfig[],
	roleId: RoleId,
	resource: PermissionResource,
): PermissionOptionKey => {
	const grants = new Set(getRoleGrants(roleConfigs, roleId))
	if (roleId !== 'employee') {
		for (const permission of getRoleGrants(roleConfigs, 'employee')) grants.add(permission)
	}

	return [...resource.options].reverse().find(option => optionMatches(grants, option))?.key ?? 'none'
}

const hasInheritedEmployeeGrant = (employeeGrants: Set<PermissionKey>, resource: PermissionResource): boolean =>
	resource.permissions.some(permission => employeeGrants.has(permission))

const applyRoleResourceValue = (
	roleConfigs: RolePermissionConfig[],
	roleId: RoleId,
	resource: PermissionResource,
	value: PermissionOptionKey,
): RolePermissionConfig[] => {
	const option = resource.options.find(o => o.key === value)
	if (!option) return roleConfigs

	const employeeGrants = new Set(getRoleGrants(roleConfigs, 'employee'))
	return roleConfigs.map(config => {
		if (config.roleId !== roleId) return config
		const preserved = config.grants.filter(permission => !resource.permissions.includes(permission))
		const nextGrants =
			roleId === 'employee' ? option.grants : option.grants.filter(permission => !employeeGrants.has(permission))
		return { ...config, grants: [...new Set([...preserved, ...nextGrants])] }
	})
}

const getOverrideResourceValue = (
	override: UserPermissionOverride | undefined,
	resource: PermissionResource,
): UserOverrideOptionKey => {
	if (!override) return 'inherit'
	if (resource.permissions.some(permission => override.revocations.includes(permission))) return 'revoke'

	const additions = new Set(override.additions)
	return (
		[...resource.options].reverse().find(option => option.key !== 'none' && optionMatches(additions, option))?.key ??
		'inherit'
	)
}

const applyOverrideResourceValue = (
	overrides: UserPermissionOverride[],
	userId: string,
	resource: PermissionResource,
	value: UserOverrideOptionKey,
): UserPermissionOverride[] => {
	const existing = overrides.find(o => o.userId === userId)
	const base: UserPermissionOverride = existing ?? { userId, additions: [], revocations: [] }
	const option = resource.options.find(o => o.key === value)
	const next: UserPermissionOverride = {
		...base,
		additions: base.additions.filter(permission => !resource.permissions.includes(permission)),
		revocations: base.revocations.filter(permission => !resource.permissions.includes(permission)),
	}

	if (value === 'revoke') next.revocations = [...next.revocations, ...resource.permissions]
	else if (option) next.additions = [...next.additions, ...option.grants]

	return existing ? overrides.map(o => (o.userId === userId ? next : o)) : [...overrides, next]
}

type ResourceRadioRowProps = {
	label: string
	description?: string
	value: string
	options: Array<{ key: string; label: string; disabled?: boolean }>
	onChange: (value: string) => void
	disabled?: boolean
}

const ResourceRadioRow = ({ label, description, value, options, onChange, disabled }: ResourceRadioRowProps) => (
	<div className='flex items-center justify-between gap-6 py-4 border-b border-border last:border-b-0'>
		<div className='min-w-0'>
			<p className='text-sm font-medium text-878787'>{label}</p>
			{description && <p className='text-xs text-878787 mt-1'>{description}</p>}
		</div>
		<RadioGroup value={value} className='flex gap-6 shrink-0' onValueChange={onChange} disabled={disabled}>
			{options.map(option => (
				<div key={option.key} className='flex items-center space-x-2'>
					<RadioGroupItem value={option.key} id={`${label}-${option.key}`} disabled={disabled || option.disabled} />
					<label htmlFor={`${label}-${option.key}`} className='text-sm font-normal text-878787 capitalize'>
						{option.label}
					</label>
				</div>
			))}
		</RadioGroup>
	</div>
)

type RolePermissionsCardProps = {
	content: ReturnType<typeof useIntlayer<'permissions-settings-page'>>
	roleId: RoleId
	roleConfigs: RolePermissionConfig[]
	employeeGrants: Set<PermissionKey>
	isOwner: boolean
}

const RolePermissionsCard = ({ content, roleId, roleConfigs, employeeGrants, isOwner }: RolePermissionsCardProps) => (
	<ScrollArea className='h-[520px] pr-3'>
		{PERMISSION_RESOURCES.map(resource => {
			const inherited = roleId !== 'employee' && hasInheritedEmployeeGrant(employeeGrants, resource)
			const isSettings = resource.permissions.includes('settings.permissions.manage')
			const rowDisabled = isSettings && !isOwner
			return (
				<ResourceRadioRow
					key={resource.key}
					label={content.resources[resource.key].title.value}
					description={
						inherited ? content.inheritedFromEmployee.value : content.resources[resource.key].description.value
					}
					value={getRoleResourceValue(roleConfigs, roleId, resource)}
					options={resource.options.map(option => ({
						key: option.key,
						label: content.options[option.key].value,
						disabled: inherited && option.key === 'none',
					}))}
					onChange={value => {
						draftRoleConfigs$.set(current =>
							applyRoleResourceValue(current, roleId, resource, value as PermissionOptionKey),
						)
					}}
					disabled={rowDisabled}
				/>
			)
		})}
	</ScrollArea>
)

type UserOverrideCardProps = {
	content: ReturnType<typeof useIntlayer<'permissions-settings-page'>>
	user: PermissionUser
	overrides: UserPermissionOverride[]
}

const UserOverrideCard = ({ content, user, overrides }: UserOverrideCardProps) => (
	<ScrollArea className='h-[520px] pr-3'>
		{PERMISSION_RESOURCES.map(resource => {
			const override = overrides.find(o => o.userId === user.id)
			return (
				<ResourceRadioRow
					key={resource.key}
					label={content.resources[resource.key].title.value}
					description={content.resources[resource.key].description.value}
					value={getOverrideResourceValue(override, resource)}
					options={[
						{ key: 'inherit', label: content.userOverrides.inherit.value },
						...resource.options
							.filter(option => option.key !== 'none')
							.map(option => ({
								key: option.key,
								label: content.options[option.key].value,
							})),
						{ key: 'revoke', label: content.userOverrides.revoke.value },
					]}
					onChange={value => {
						draftUserOverrides$.set(current =>
							applyOverrideResourceValue(current, user.id, resource, value as UserOverrideOptionKey),
						)
					}}
				/>
			)
		})}
	</ScrollArea>
)

const PermissionsSettingsPage = () => {
	const content = useIntlayer('permissions-settings-page')
	const user = useSelector(() => session$.user.get())
	const isOwner = user?.companyRole === 'owner'

	const { config } = usePermissionConfig()
	const { save } = usePermissionConfigActions()
	const { users } = usePermissionUsers()

	const draftRoleConfigs = useSelector(draftRoleConfigs$)
	const draftUserOverrides = useSelector(draftUserOverrides$)

	const [selectedRole, setSelectedRole] = useState<RoleId>('employee')
	const [selectedUserId, setSelectedUserId] = useState(NO_USER)
	const [savingRoles, setSavingRoles] = useState(false)
	const [savingOverrides, setSavingOverrides] = useState(false)

	const employeeGrants = useMemo(() => new Set(getRoleGrants(draftRoleConfigs, 'employee')), [draftRoleConfigs])
	const selectedUser = useMemo(() => users.find(u => u.id === selectedUserId) ?? null, [users, selectedUserId])

	const handleSaveRoles = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
		e.preventDefault()
		setSavingRoles(true)
		await save({ ...config, roleConfigs: draftRoleConfigs })
		setSavingRoles(false)
	}

	const handleSaveOverrides = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
		e.preventDefault()
		setSavingOverrides(true)
		await save({ ...config, userOverrides: draftUserOverrides })
		setSavingOverrides(false)
	}

	return (
		<div className='space-y-6 mt-4'>
			<form onSubmit={handleSaveRoles}>
				<Card>
					<CardHeader>
						<CardTitle>{content.title}</CardTitle>
						<CardDescription>{content.description}</CardDescription>
					</CardHeader>
					<CardContent>
						<Tabs value={selectedRole} onValueChange={value => setSelectedRole(value as RoleId)} className='w-full'>
							<TabsList className='w-full flex mb-4'>
								{ROLES.map(roleId => (
									<TabsTrigger key={roleId} value={roleId} className='flex-1'>
										{content.roles[roleId]}
									</TabsTrigger>
								))}
							</TabsList>
							{ROLES.map(roleId => (
								<TabsContent key={roleId} value={roleId}>
									<RolePermissionsCard
										content={content}
										roleId={roleId}
										roleConfigs={draftRoleConfigs}
										employeeGrants={employeeGrants}
										isOwner={isOwner}
									/>
								</TabsContent>
							))}
						</Tabs>
					</CardContent>
					<CardFooter className='justify-between'>
						<span>{content.ownerLocked}</span>
						<SubmitButton type='submit' size='sm' isSubmitting={savingRoles}>
							{savingRoles ? content.saving : content.save}
						</SubmitButton>
					</CardFooter>
				</Card>
			</form>

			<form onSubmit={handleSaveOverrides}>
				<Card>
					<CardHeader>
						<CardTitle>{content.userOverrides.title}</CardTitle>
						<CardDescription>{content.userOverrides.description}</CardDescription>
					</CardHeader>
					<CardContent className='space-y-4'>
						<Select value={selectedUserId} onValueChange={setSelectedUserId}>
							<SelectTrigger className='w-full md:w-80 text-xs'>
								<SelectValue placeholder={content.userOverrides.addUser.value} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={NO_USER}>{content.userOverrides.selectUser}</SelectItem>
								{users.map(permissionUser => (
									<SelectItem key={permissionUser.id} value={permissionUser.id}>
										{permissionUser.fullName ?? permissionUser.email}
									</SelectItem>
								))}
							</SelectContent>
						</Select>

						{selectedUser ? (
							<UserOverrideCard content={content} user={selectedUser} overrides={draftUserOverrides} />
						) : (
							<p className='text-sm text-878787'>{content.userOverrides.noOverrides}</p>
						)}
					</CardContent>
					<CardFooter className='justify-end'>
						<SubmitButton type='submit' size='sm' isSubmitting={savingOverrides} disabled={!selectedUser}>
							{savingOverrides ? content.saving : content.save}
						</SubmitButton>
					</CardFooter>
				</Card>
			</form>
		</div>
	)
}

export { PermissionsSettingsPage }
